<script lang="ts">
  import type { InboxWorkflowTableMetricsProps } from "@kontextwerk/control-web-frame/inbox-workflow/contract"
  import CostBenefitNumbersBlock from "../../../../../../../../../../../../_shared/_modules/cost-benefit-numbers-block"
  import {
    estimateWorkOrderPrintDelivery,
    type WorkOrderPrintDeliveryEstimate,
  } from "../../../../../../../../../../../../../../api/kehrblatt/letters"
  import type { LetterDeliveryDecisionDraft, LetterDeliveryDecisionRow } from "../../contract"
  import {
    buildLetterDeliveryEstimatePayload,
    type LetterDeliveryEstimatePayload,
  } from "./_logic/estimate"
  import { summarizeLetterDeliveryDecisions } from "./_logic/decisionSummary"

  let {
    rows = [],
    runnableRows = [],
    readOnly = false,
    draftFor,
    organizationId = null,
    automaticLetterDeliveryAllowed = true,
    automaticLetterDeliveryDisabledReason = null,
  }: InboxWorkflowTableMetricsProps<LetterDeliveryDecisionRow, LetterDeliveryDecisionDraft> & {
    automaticLetterDeliveryAllowed?: boolean
    automaticLetterDeliveryDisabledReason?: string | null
  } = $props()

  let estimate = $state<WorkOrderPrintDeliveryEstimate | null>(null)
  let loading = $state(false)
  let error = $state<string | null>(null)
  let requestId = 0
  let debounce: ReturnType<typeof setTimeout> | null = null
  const summary = $derived(
    summarizeLetterDeliveryDecisions(
      runnableRows,
      draftFor,
      automaticLetterDeliveryAllowed,
      automaticLetterDeliveryDisabledReason,
    ),
  )

  const deliveryDecision = $derived(
    buildLetterDeliveryEstimatePayload(rows, draftFor, automaticLetterDeliveryAllowed),
  )
  const estimateSignature = $derived(
    JSON.stringify({
      organizationId,
      status: deliveryDecision.status,
      letterDeliveryBatchItemIds:
        deliveryDecision.status === "ready" ? deliveryDecision.letterDeliveryBatchItemIds : [],
      automaticDeliveryItemIds:
        deliveryDecision.status === "ready" ? deliveryDecision.automaticDeliveryItemIds : [],
    }),
  )

  const clearDebounce = () => {
    if (!debounce) return

    clearTimeout(debounce)
    debounce = null
  }

  const loadEstimate = async (
    targetOrganizationId: string,
    payload: LetterDeliveryEstimatePayload,
    signature: string,
  ) => {
    const currentRequestId = ++requestId
    try {
      const result = await estimateWorkOrderPrintDelivery(
        targetOrganizationId,
        payload.letterDeliveryBatchItemIds,
        payload.automaticDeliveryItemIds,
      )

      if (currentRequestId !== requestId || signature !== estimateSignature) return

      estimate = result
      error = null
    } catch (err) {
      if (currentRequestId !== requestId || signature !== estimateSignature) return

      console.error("letter_delivery_decision_estimate_failed", err)
      estimate = null
      error = "Kosten-Nutzen-Schätzung konnte nicht berechnet werden."
    } finally {
      if (currentRequestId === requestId && signature === estimateSignature) {
        loading = false
      }
    }
  }

  $effect(() => {
    const signature = estimateSignature
    const payload = deliveryDecision
    const targetOrganizationId = organizationId
    clearDebounce()

    if (!targetOrganizationId || !rows.length) {
      requestId += 1
      estimate = null
      error = null
      loading = false

      return
    }

    if (payload.status === "loading") {
      requestId += 1
      estimate = null
      error = null
      loading = true

      return
    }

    if (payload.status === "error") {
      requestId += 1
      estimate = null
      error = payload.detail
      loading = false

      return
    }

    if (!payload.letterDeliveryBatchItemIds.length) {
      requestId += 1
      estimate = null
      error = null
      loading = false

      return
    }

    loading = true
    debounce = setTimeout(() => void loadEstimate(targetOrganizationId, payload, signature), 150)

    return clearDebounce
  })
</script>

{#if !readOnly}
  <div class="decision-summary" aria-live="polite">
    <p
      >Beim Speichern: <strong>{summary.automatic} automatisch</strong>,
      <strong>{summary.manual} händisch</strong>.</p
    >
    {#if summary.blocked}
      <p role="alert">Speichern blockiert: {summary.blocked} Zustellentscheidung(en) prüfen.</p>
      <ul>
        {#each summary.reasons as reason}
          <li>{reason}</li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}

<CostBenefitNumbersBlock
  estimate={estimate?.cost_benefit ?? null}
  {loading}
  {error}
  darkMode={true}
/>

<style>
  .decision-summary {
    margin-bottom: 0.75rem;
    overflow-wrap: anywhere;
  }
  .decision-summary p {
    margin: 0.25rem 0;
  }
  .decision-summary ul {
    margin: 0.25rem 0;
    padding-left: 1.25rem;
  }
</style>
